import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';
// Chemin corrigé - remonte d'un niveau car vous êtes dans components/home/
import ProblemSection from '../ProblemSection/ProblemSection';
import MethodSection from '../MethodSection/MethodSection';
import TestimonialsSection from '../TestimonialsSection/TestimonialsSection';
import FinalCTASection from '../FinalCTASection/FinalCTASection';
import ExpertiseSection from '../ExpertiseSection/ExpertiseSection';
import Header from '../Header/Header';
import Footer from '../footer/footer';
import TopAnnouncementBar from '../TopAnnouncementBar/TopAnnouncementBar';
import WhatsAppButton from '../whatsapp/WhatsAppButton';
import SolutionSection from '../SolutionSection/SolutionSection';
import OffersSection from '../OffersSection/OffersSection';
import ForWhomSection from '../ForWhomSection/ForWhomSection';
import { 
  MdEmail, 
  MdTableChart, 
  MdPeople, 
  MdRepeat, 
  MdAttachMoney,
  MdCheckCircle,
  MdDeviceHub,
  MdTrendingUp,
  MdRocketLaunch,
  MdArrowForward,
  MdPlayCircleOutline,
  MdOutlineTimer,
  MdTimeline,
  MdAnalytics,
  MdCode,
  MdDesignServices,
  MdStorage,
  MdSecurity,
  MdCloudSync,
  MdSpeed,
  MdPhoneIphone,
  MdWeb,
  MdBrush,
  MdInsights,
  MdSupportAgent,
  MdIntegrationInstructions,
  MdAutoAwesome,
  MdDataUsage,
  MdNetworkCheck,
  MdBuild,
  MdStar,
  MdVerified,
  MdSmartToy,
  MdOutlineAutoAwesome,
  MdFlashOn
} from 'react-icons/md';
import { FaChartLine, FaRobot, FaBrain, FaUsers, FaShieldAlt, FaDocker, FaAws, FaGitAlt, FaStar, FaTrophy, FaCrown, FaMagic, FaRocket } from 'react-icons/fa';
import { SiNextdotjs, SiReact, SiNodedotjs, SiMongodb, SiTailwindcss, SiFigma, SiPostgresql, SiRedis, SiKubernetes, SiJenkins, SiTerraform } from 'react-icons/si';
import './Home.css';

const HeroBanner = () => {
  const [activeService, setActiveService] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const containerRef = useRef(null);
  const autoPlayIntervalRef = useRef(null);
  
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  const springConfig = { damping: 25, stiffness: 150 };
  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [15, -15]), springConfig);
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-15, 15]), springConfig);

  // Services actualisés pour parler d'automatisation
  const services = [
    { 
      icon: <FaRobot />, 
      name: "Automatisation IA", 
      description: "Workflows intelligents qui transforment vos processus métier et votre prospection.",
      color: "#3b82f6",
      features: ["Prospection auto", "IA conversationnelle", "ROI immédiat"]
    },
    { 
      icon: <MdRepeat />, 
      name: "Relances Automatiques", 
      description: "Séquences de relances intelligentes qui ne laissent aucune opportunité passer.",
      color: "#8b5cf6",
      features: ["Relances ciblées", "Timing optimal", "+45% conversion"]
    },
    { 
      icon: <MdPeople />, 
      name: "Suivi CRM Automatisé", 
      description: "Centralisez et suivez vos prospects sans effort grâce à l'automatisation.",
      color: "#10b981",
      features: ["Centralisation", "Pipeline auto", "Tableaux de bord"]
    },
    { 
      icon: <MdOutlineAutoAwesome />, 
      name: "Communication IA", 
      description: "Messages personnalisés sur WhatsApp, email et réseaux sociaux.",
      color: "#ec4899",
      features: ["Multi-canaux", "Personnalisation", "24/7 automatisé"]
    },
    { 
      icon: <MdAnalytics />, 
      name: "Performance & Analytics", 
      description: "Suivez vos résultats en temps réel pour optimiser votre acquisition.",
      color: "#f59e0b",
      features: ["Dashboard temps réel", "KPIs automatisés", "Rapports IA"]
    },
    { 
      icon: <MdTrendingUp />, 
      name: "Croissance Automatisée", 
      description: "Un système complet qui travaille pour attirer et convertir plus de clients.",
      color: "#06b6d4",
      features: ["Acquisition auto", "Ventes optimisées", "Scalabilité"]
    }
  ];

  // Auto-play functionality
  useEffect(() => {
    if (isAutoPlaying) {
      autoPlayIntervalRef.current = setInterval(() => {
        setActiveService((prev) => (prev + 1) % services.length);
      }, 4000);
    }

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
    };
  }, [isAutoPlaying, services.length]);

  const handleCarouselMouseEnter = () => {
    setIsAutoPlaying(false);
  };

  const handleCarouselMouseLeave = () => {
    setIsAutoPlaying(true);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        mouseX.set(x);
        mouseY.set(y);
        setMousePosition({ x: e.clientX - rect.left, y: e.clientY - rect.top });
      }
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [mouseX, mouseY]);

  const technologies = [
    { icon: <SiReact />, name: "React", color: "#61dafb" },
    { icon: <SiNextdotjs />, name: "Next.js", color: "#ffffff" },
    { icon: <SiTailwindcss />, name: "Tailwind", color: "#38bdf8" },
    { icon: <SiFigma />, name: "Figma", color: "#f24e1e" },
    { icon: <SiNodedotjs />, name: "Node.js", color: "#68a063" },
    { icon: <SiPostgresql />, name: "PostgreSQL", color: "#4169e1" },
    { icon: <SiMongodb />, name: "MongoDB", color: "#47a248" },
    { icon: <SiRedis />, name: "Redis", color: "#dc382d" },
    { icon: <FaDocker />, name: "Docker", color: "#2496ed" },
    { icon: <SiKubernetes />, name: "Kubernetes", color: "#326ce5" },
    { icon: <FaAws />, name: "AWS", color: "#ff9900" },
    { icon: <SiJenkins />, name: "Jenkins", color: "#d33833" },
    { icon: <SiTerraform />, name: "Terraform", color: "#7b42bc" },
    { icon: <FaGitAlt />, name: "GitHub Actions", color: "#2088ff" }
  ];

  const statsData = [
    { value: "+200%", label: "Productivité", icon: <MdSpeed /> },
    { value: "24/7", label: "Automatisation", icon: <MdFlashOn /> },
    { value: "-15h", label: "Gain de temps", icon: <MdOutlineTimer /> },
    { value: "100%", label: "Satisfaction", icon: <MdVerified /> }
  ];

  return (
    <>
       <WhatsAppButton />
      <TopAnnouncementBar />
      <Header />
      <section className="hero-banner-root" ref={containerRef}>
        <div className="hero-gradient-wrapper">
          <div className="gradient-sphere sphere-1"></div>
          <div className="gradient-sphere sphere-2"></div>
          <div className="gradient-sphere sphere-3"></div>
          <div className="gradient-sphere sphere-4"></div>
          <div className="gradient-sphere sphere-5"></div>
        </div>

        <motion.div 
          className="cursor-glow-effect"
          animate={{
            x: mousePosition.x - 250,
            y: mousePosition.y - 250,
          }}
          transition={{ type: "spring", damping: 20, stiffness: 200 }}
        />

        <div className="hero-main-container">
          <div className="hero-grid-layout">
            <motion.div 
              className="hero-text-content"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div 
                className="hero-badge-wrapper"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <FaRobot className="badge-icon-style" />
                <span>Expert en automatisation IA</span>
                <MdVerified className="verified-icon" />
              </motion.div>

              <motion.h1 
                className="hero-main-title"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <span className="gradient-text-effect">ETEF Design,</span> moins d'efforts,
                <br />plus de temps et <span className="highlight-text-effect">plus de clients.</span>
              </motion.h1>

              <motion.p 
                className="hero-description-text"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Nous aidons les entreprises à automatiser leur prospection, leurs relances, 
                leurs ventes et leur communication grâce à l'IA.
              </motion.p>

              <motion.p 
                className="hero-description-text hero-secondary-text"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.45 }}
                style={{ fontSize: "0.9rem", color: "#7A8DAB", marginTop: "-10px" }}
              >
                Aujourd'hui, beaucoup d'entreprises perdent des prospects simplement parce qu'elles 
                ne relancent pas assez ou pas au bon moment. Nous mettons en place des systèmes 
                simples qui travaillent pour vous.
              </motion.p>

              <motion.div 
                className="hero-action-buttons"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <motion.button 
                  className="primary-action-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Automatiser mon activité <MdArrowForward className="btn-icon-style" />
                </motion.button>
                <motion.button 
                  className="secondary-action-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <MdPlayCircleOutline /> Découvrir nos solutions IA
                </motion.button>
              </motion.div>
            </motion.div>

            <motion.div 
              className="services-showcase-area"
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div 
                className="services-carousel-container"
                onMouseEnter={handleCarouselMouseEnter}
                onMouseLeave={handleCarouselMouseLeave}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeService}
                    className="service-card-wrapper"
                    initial={{ opacity: 0, y: 50, rotateX: 20 }}
                    animate={{ opacity: 1, y: 0, rotateX: 0 }}
                    exit={{ opacity: 0, y: -50, rotateX: -20 }}
                    transition={{ duration: 0.5 }}
                    style={{ background: `linear-gradient(135deg, ${services[activeService].color}20, rgba(26,45,80,0.6))` }}
                  >
                    <div className="service-icon-display" style={{ color: services[activeService].color }}>
                      {services[activeService].icon}
                    </div>
                    <h3 className="service-title-name">{services[activeService].name}</h3>
                    <p className="service-description-text">{services[activeService].description}</p>
                    <div className="service-features-list">
                      {services[activeService].features.map((feature, idx) => (
                        <motion.span 
                          key={idx} 
                          className="feature-tag-item"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                        >
                          {feature}
                        </motion.span>
                      ))}
                    </div>
                  </motion.div>
                </AnimatePresence>

                <div className="auto-play-indicator">
                  <div className="auto-play-progress" style={{ animation: isAutoPlaying ? `progress 4s linear infinite` : 'none' }} />
                </div>

                <div className="carousel-controls-wrapper">
                  {services.map((service, index) => (
                    <motion.button
                      key={index}
                      className={`carousel-nav-dot ${activeService === index ? 'active' : ''}`}
                      onClick={() => {
                        setActiveService(index);
                        setIsAutoPlaying(false);
                        setTimeout(() => setIsAutoPlaying(true), 5000);
                      }}
                      whileHover={{ scale: 1.3 }}
                      whileTap={{ scale: 0.9 }}
                      style={{ backgroundColor: activeService === index ? service.color : "#ffffff40" }}
                    />
                  ))}
                </div>
              </div>

              <div className="floating-stats-grid">
                {statsData.map((stat, index) => (
                  <motion.div
                    key={index}
                    className="stat-card-item"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1 + index * 0.1 }}
                    whileHover={{ y: -5, scale: 1.05 }}
                  >
                    <div className="stat-icon-wrapper">{stat.icon}</div>
                    <div className="stat-info">
                      <span className="stat-number-value">{stat.value}</span>
                      <span className="stat-label-text">{stat.label}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Partners section */}
          <motion.div 
            className="partners-section-wrapper"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <div className="section-header">
              <FaTrophy className="section-icon" />
              <p className="partners-label-text">Ils nous font confiance</p>
              <div className="section-line"></div>
            </div>
            <div className="partners-marquee-container">
              <div className="marquee-scroll-content">
                {[...Array(3)].map((_, i) => (
                  <React.Fragment key={i}>
                    <span className="partner-name-text">L'Oréal</span>
                    <MdStar className="partner-star" />
                    <span className="partner-name-text">TotalEnergies</span>
                    <MdStar className="partner-star" />
                    <span className="partner-name-text">BNP Paribas</span>
                    <MdStar className="partner-star" />
                    <span className="partner-name-text">Decathlon</span>
                    <MdStar className="partner-star" />
                    <span className="partner-name-text">Sephora</span>
                    <MdStar className="partner-star" />
                  </React.Fragment>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Technologies section */}
          <motion.div 
            className="technologies-section-wrapper"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.4 }}
          >
            <div className="section-header">
              <FaCrown className="section-icon" />
              <p className="technologies-label-text">Technologies de pointe</p>
              <div className="section-line"></div>
            </div>
            <div className="technologies-marquee-container">
              <div className="marquee-scroll-content-reverse">
                {[...Array(3)].map((_, i) => (
                  <React.Fragment key={i}>
                    {technologies.map((tech, idx) => (
                      <div key={idx} className="tech-marquee-item">
                        <div className="tech-marquee-icon" style={{ color: tech.color }}>
                          {tech.icon}
                        </div>
                        <span className="tech-marquee-name">{tech.name}</span>
                      </div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      <ProblemSection />
      <SolutionSection />
      <OffersSection />
      <ForWhomSection />
      <ExpertiseSection />
      <MethodSection />
      <TestimonialsSection />
      <FinalCTASection />
      <Footer />

    </>
  );
};

export default HeroBanner;